import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { List, X } from "@phosphor-icons/react";
import { Logo } from "@/components/site/Logo";
import { SECTIONS, usePageNav } from "@/components/site/nav";
import { useStoreCTA } from "@/components/site/ComingSoon";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { goToSection } = usePageNav();
  const { open: openComingSoon } = useStoreCTA();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (id) => {
    setOpen(false);
    goToSection(id);
  };

  const handleDownload = () => {
    setOpen(false);
    openComingSoon();
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      className="fixed inset-x-0 top-0 z-50 flex justify-center px-4 pt-3 sm:pt-4"
      data-testid="site-navbar"
    >
      <nav
        className={`flex w-full max-w-6xl items-center justify-between rounded-2xl px-4 py-3 transition-colors duration-500 sm:px-6 ${
          scrolled ? "glass-strong shadow-[0_10px_40px_-20px_rgba(0,0,0,0.9)]" : "border border-transparent"
        }`}
      >
        <Logo />

        <div className="hidden items-center gap-8 lg:flex">
          {SECTIONS.map((s) => (
            <button
              key={s.id}
              onClick={() => handleNav(s.id)}
              data-testid={`nav-${s.id}`}
              className="relative text-sm font-medium text-white/70 transition-colors hover:text-white after:absolute after:-bottom-1.5 after:left-0 after:h-px after:w-0 after:bg-teal-400 after:transition-all after:duration-300 hover:after:w-full"
            >
              {s.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={handleDownload}
            data-testid="nav-download-btn"
            className="hidden rounded-full bg-teal-500 px-5 py-2.5 text-sm font-semibold text-midnight transition-transform duration-300 hover:scale-[1.04] hover:bg-teal-400 sm:inline-flex"
          >
            Download App
          </button>
          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
            className="grid h-10 w-10 place-items-center rounded-xl border border-white/12 text-white lg:hidden"
          >
            {open ? <X size={20} /> : <List size={20} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25 }}
            className="glass-strong absolute inset-x-4 top-[76px] z-50 rounded-2xl p-4 lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-1">
              {SECTIONS.map((s) => (
                <button
                  key={s.id}
                  onClick={() => handleNav(s.id)}
                  className="rounded-xl px-4 py-3 text-left text-base font-medium text-white/80 transition-colors hover:bg-white/5 hover:text-white"
                >
                  {s.label}
                </button>
              ))}
              <button
                onClick={handleDownload}
                data-testid="mobile-nav-download-btn"
                className="mt-2 rounded-xl bg-teal-500 px-4 py-3 text-center font-semibold text-midnight"
              >
                Download App
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
